/**
 * 视频提示词输入框（富文本版）
 *
 * 支持插入 @图N 引用 chip：用户可以从已上传的参考图列表里选一张插入光标处，
 * chip 渲染为小缩略图 + 序号；最终提交给后端的还是纯文本形式 @图N。
 *
 * 同时支持 AI 优化提示词（流式输出）。
 *
 * 上限 4000 字（与后端 Prompt 校验一致）
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { promptOptimizeAPI } from '../../../services/api';
import toast from '../../../components/Toast';

interface VideoPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** 当前模式，用于 AI 优化时选择对应的 system prompt */
  mode?: 'text2video' | 'img2video' | 'first_last_frame';
  /** 已上传的参考图列表，用于渲染 @图N 引用 chip 的缩略图 */
  referenceImages?: string[];
  /** 渲染缩略图时如果是本地 API URL 需要附加 token */
  resolveImageSrc?: (raw: string) => string;
}

const MAX_LENGTH = 4000;

// 匹配 @图1 / @图 1 / @image1 / @img1（兼容多种写法）
const REF_RE = /@(?:图|image|img)\s*(\d+)/gi;

export function VideoPromptInput({
  value,
  onChange,
  placeholder = '描述你想生成的视频内容；可点击下方"插入引用"在 prompt 里指代具体参考图',
  mode = 'text2video',
  referenceImages,
  resolveImageSrc,
}: VideoPromptInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastSyncedTextRef = useRef<string>('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeEnabled, setOptimizeEnabled] = useState(false);

  useEffect(() => {
    promptOptimizeAPI
      .getStatus()
      .then((res) => setOptimizeEnabled(res.isEnabled))
      .catch(() => setOptimizeEnabled(false));
  }, []);

  // 把纯文本（含 @图N）渲染成 DOM（chip + 文本节点交替）
  const renderToEditor = useCallback(
    (text: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      // 清空
      while (editor.firstChild) editor.removeChild(editor.firstChild);

      let lastIndex = 0;
      let match: RegExpExecArray | null;
      const re = new RegExp(REF_RE.source, REF_RE.flags);
      while ((match = re.exec(text)) !== null) {
        // 前置文本
        if (match.index > lastIndex) {
          editor.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }
        // chip
        editor.appendChild(buildChip(match[1], referenceImages, resolveImageSrc));
        lastIndex = match.index + match[0].length;
      }
      // 尾部文本
      if (lastIndex < text.length) {
        editor.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      lastSyncedTextRef.current = text;
    },
    [referenceImages, resolveImageSrc]
  );

  // 父组件 value 变化时重新渲染（但避免自身输入触发的 value 变化导致重渲染、丢光标）
  useEffect(() => {
    if (value === lastSyncedTextRef.current) return;
    renderToEditor(value);
  }, [value, renderToEditor]);

  // DOM → 纯文本
  const serializeEditor = useCallback((): string => {
    const editor = editorRef.current;
    if (!editor) return '';
    let result = '';
    editor.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const refIndex = el.getAttribute('data-ref-index');
        if (refIndex) {
          result += `@图${refIndex}`;
        } else {
          // 兜底：用户粘贴进来的其他元素，尝试取 textContent
          result += el.textContent || '';
        }
      }
    });
    return result;
  }, []);

  const handleInput = () => {
    const text = serializeEditor();
    if (text.length > MAX_LENGTH) {
      // 超长：撤销最后一次输入（简单粗暴，回滚到上次同步的状态）
      renderToEditor(lastSyncedTextRef.current);
      return;
    }
    lastSyncedTextRef.current = text;
    onChange(text);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    // 只接受纯文本粘贴，避免外部带入富文本
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // 在光标位置插入 chip
  const insertReference = (refIndex: number) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const chip = buildChip(String(refIndex), referenceImages, resolveImageSrc);
    const space = document.createTextNode(' ');

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(space);
      range.insertNode(chip);
      // 光标移到空格之后
      range.setStartAfter(space);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editor.appendChild(chip);
      editor.appendChild(space);
    }
    handleInput();
  };

  const handleOptimize = async () => {
    if (!value.trim() || isOptimizing) return;
    setIsOptimizing(true);
    let optimizedText = '';
    try {
      await promptOptimizeAPI.streamOptimize(
        value,
        mode,
        (chunk) => {
          optimizedText += chunk;
          onChange(optimizedText);
        },
        (error) => {
          console.error('优化失败:', error);
          toast.error('提示词优化失败: ' + error);
        },
        () => setIsOptimizing(false)
      );
    } catch (error) {
      setIsOptimizing(false);
      toast.error('优化失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  const refCount = referenceImages?.length || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-light-text">提示词</span>
        <span className="text-xs text-light-muted">
          {value.length} / {MAX_LENGTH}
        </span>
      </div>
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!isOptimizing}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          className={`input-field w-full leading-relaxed min-h-[6rem] outline-none ${
            isOptimizing ? 'bg-blue-50/50' : ''
          }`}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        />
        {value.length === 0 && (
          <div className="pointer-events-none absolute top-3 left-4 text-light-muted text-sm">
            {placeholder}
          </div>
        )}
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          {value && !isOptimizing && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="p-1.5 text-light-muted hover:text-light-text rounded-lg hover:bg-light-hover"
              title="清空"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {optimizeEnabled && (
            <button
              type="button"
              onClick={handleOptimize}
              disabled={isOptimizing || !value.trim()}
              className={`p-1.5 transition-colors rounded-lg ${
                isOptimizing
                  ? 'text-accent-blue animate-pulse bg-blue-100'
                  : value.trim()
                  ? 'text-light-muted hover:text-accent-blue hover:bg-light-hover'
                  : 'text-light-muted/50 cursor-not-allowed'
              }`}
              title={isOptimizing ? 'AI优化中...' : 'AI优化提示词'}
            >
              <Sparkles className={`w-4 h-4 ${isOptimizing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {refCount > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-light-muted mr-1">插入引用：</span>
          {Array.from({ length: refCount }).map((_, idx) => {
            const num = idx + 1;
            const raw = referenceImages![idx];
            const src = resolveImageSrc ? resolveImageSrc(raw) : raw;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => insertReference(num)}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-light-border bg-white hover:border-accent-blue hover:bg-accent-blue/5 text-xs"
                title={`插入 @图${num}`}
              >
                <img src={src} alt="" className="w-5 h-5 rounded object-cover" />
                <span className="text-light-text">@图{num}</span>
              </button>
            );
          })}
        </div>
      )}

      {isOptimizing && (
        <p className="text-xs text-accent-blue mt-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3 animate-spin" />
          AI 正在优化提示词...
        </p>
      )}
    </div>
  );
}

function buildChip(
  refIndex: string,
  referenceImages: string[] | undefined,
  resolveImageSrc?: (raw: string) => string
): HTMLSpanElement {
  const idx = parseInt(refIndex, 10);
  const chip = document.createElement('span');
  chip.setAttribute('contenteditable', 'false');
  chip.setAttribute('data-ref-index', String(idx));
  chip.className =
    'inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 rounded-md bg-accent-blue/10 text-accent-blue text-xs align-middle select-none';
  chip.style.cursor = 'default';

  // 缩略图
  const raw = referenceImages?.[idx - 1];
  if (raw) {
    const img = document.createElement('img');
    img.src = resolveImageSrc ? resolveImageSrc(raw) : raw;
    img.alt = '';
    img.className = 'w-4 h-4 rounded object-cover';
    chip.appendChild(img);
  }
  const label = document.createElement('span');
  label.textContent = `图${idx}`;
  chip.appendChild(label);

  return chip;
}
